/** @jsxImportSource theme-ui */
import React from 'react';
import { Grid, List } from 'semantic-ui-react';

import * as dto from 'client/dto';
import { IsLocationProximalToUserLocations } from 'utils/modelHelpers';
import { locationTypePrint } from 'utils/stringFormattingHelpers';

import { SectionHeader } from 'components/_common/SectionHeader';
import { Typography } from 'components/_common/Typography';
import {
  CaseCountDisplayCases,
  CaseCountDisplayDeaths,
} from 'components/_controls/CaseCountDisplay';

interface OutbreakSurveillanceOverallProps {
  caseCounts: dto.CaseCountModel;
  eventLocations: dto.EventLocationModel[];
  userLocationIds?: number[];
}

const OutbreakSurveillanceOverall: React.FC<OutbreakSurveillanceOverallProps> = ({
  caseCounts,
  eventLocations,
  userLocationIds = [],
}) => {
  return (
    <>
      <SectionHeader>Surveillance Summary</SectionHeader>

      <Grid columns={2} divided="vertically">
        <Grid.Row divided>
          <Grid.Column>
            <Typography variant="body2" color="stone90">
              Reported cases
            </Typography>
            <CaseCountDisplayCases caseCounts={caseCounts} />
          </Grid.Column>
          <Grid.Column>
            <Typography variant="body2" color="stone90">
              Reported deaths
            </Typography>
            <CaseCountDisplayDeaths caseCounts={caseCounts} />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {eventLocations?.length > 0 && (
        <div sx={{ mt: '24px' }}>
          <SectionHeader>By Location</SectionHeader>
          <List className="xunpadded">
            {eventLocations.map(
              ({
                countryDiseaseActivityKey,
                geonameId,
                provinceId,
                locationName,
                locationType,
                caseCounts: caseCountsInRegion,
              }) => (
                <List.Item key={geonameId}>
                  <Typography
                    variant="subtitle2"
                    color={
                      IsLocationProximalToUserLocations(
                        {
                          locationId: geonameId,
                          provinceId,
                          countryId: countryDiseaseActivityKey.countryId,
                        },
                        userLocationIds
                      )
                        ? 'seafoam100'
                        : 'deepSea70'
                    }
                  >
                    {locationName}
                  </Typography>
                  <Typography variant="caption" color="deepSea50">
                    {locationTypePrint(locationType)}
                  </Typography>
                  <Grid columns={2} divided="vertically">
                    <Grid.Row divided>
                      <Grid.Column>
                        <Typography variant="body2" color="stone90">
                          Reported cases
                        </Typography>
                        <CaseCountDisplayCases
                          caseCounts={caseCountsInRegion}
                          locationType={locationType}
                          smallDisplay
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Typography variant="body2" color="stone90">
                          Reported deaths
                        </Typography>
                        <CaseCountDisplayDeaths
                          caseCounts={caseCountsInRegion}
                          locationType={locationType}
                          smallDisplay
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </List.Item>
              )
            )}
          </List>
        </div>
      )}
    </>
  );
};

export default OutbreakSurveillanceOverall;
